import { create } from "zustand";
import { persist } from "zustand/middleware";
import { wishlistService, authService } from "../api/gameService";
import { STORAGE_KEYS, ERROR_MESSAGES } from "../config/constants";
import type { AuthUser } from "../api/types";

/* ── Wishlist store (GS-307) ──────────────────────────── */
interface WishlistState {
  ids: string[];
  toggle: (id: string) => void;
  has: (id: string) => boolean;
}

export const useWishlistStore = create<WishlistState>()(
  persist(
    (set, get) => ({
      ids: [],
      has: (id) => get().ids.includes(id),
      toggle: async (id) => {
        const inList = get().ids.includes(id);
        try {
          if (inList) {
            await wishlistService.remove(id);
            set((s) => ({ ids: s.ids.filter((x) => x !== id) }));
          } else {
            await wishlistService.add(id);
            set((s) => ({ ids: [...s.ids, id] }));
          }
        } catch (error) {
          console.error('Wishlist operation failed:', error);
          // Could add toast notification here
        }
      },
    }),
    { name: STORAGE_KEYS.WISHLIST, version: 1 }
  )
);

/* ── Auth store (GS-E1) ───────────────────────────────── */
interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (googleIdToken: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,
      clearError: () => set({ error: null }),
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const res = await authService.login(email, password) as { token: string; user: AuthUser };
          set({ user: res.user, token: res.token, isLoading: false });
        } catch (e) {
          const errorMessage = e instanceof Error ? e.message : ERROR_MESSAGES.AUTH;
          set({ error: errorMessage, isLoading: false });
        }
      },
      loginWithGoogle: async (googleIdToken) => {
        set({ isLoading: true, error: null });
        try {
          const res = await authService.loginWithGoogle(googleIdToken) as { token: string; user: AuthUser };
          set({ user: res.user, token: res.token, isLoading: false });
        } catch (e) {
          const errorMessage = e instanceof Error ? e.message : ERROR_MESSAGES.AUTH;
          set({ error: errorMessage, isLoading: false });
        }
      },
      logout: async () => {
        try {
          await authService.logout();
        } catch (error) {
          console.error('Logout failed:', error);
        } finally {
          set({ user: null, token: null });
        }
      },
    }),
    {
      name: STORAGE_KEYS.AUTH_TOKEN,
      version: 1,
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          // Migration from version 0 to 1
          return {
            user: null,
            token: null,
            isLoading: false,
            error: null,
          };
        }
        return persistedState;
      },
    }
  )
);
